import { ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";

interface Metric {
  label: string;
  value: string;
}

export interface TopTeammate {
  name: string;
  convos: string;
  hours: string;
}

interface Props {
  metrics: Metric[];
  topTeammates: TopTeammate[];
  onViewAllTeammates?: () => void;
  onTeammateClick?: (name: string) => void;
}

export function TeamPerformance({
  metrics,
  topTeammates,
  onViewAllTeammates,
  onTeammateClick,
}: Props) {
  return (
    <Card className="p-6 space-y-5" data-testid="team-performance">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">Team Performance</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Communication and response metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="text-xs px-2 py-1.5 border border-border rounded-md bg-card text-muted-foreground"
            data-testid="team-agencies-filter"
            defaultValue="all"
          >
            <option value="all">All Agencies</option>
          </select>
          <select
            className="text-xs px-2 py-1.5 border border-border rounded-md bg-card text-muted-foreground"
            data-testid="team-teams-filter"
            defaultValue="all"
          >
            <option value="all">All Teams</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {metrics.map((m) => (
          <div
            key={m.label}
            className="rounded-md bg-muted/30 border border-border/50 px-3 py-3"
            data-testid={`team-metric-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <p className="text-xs text-muted-foreground">{m.label}</p>
            <p className="text-lg font-semibold mt-0.5">{m.value}</p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <button
          type="button"
          onClick={onViewAllTeammates}
          className="inline-flex items-center gap-1 text-sm font-semibold hover:underline cursor-pointer"
          data-testid="team-top-teammates-toggle"
        >
          Top Teammates
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </button>

        {topTeammates.length === 0 ? (
          <p className="text-xs text-muted-foreground">No teammate activity yet.</p>
        ) : (
          <ul className="space-y-2" data-testid="team-top-teammates-list">
            {topTeammates.map((t, i) => (
              <li key={t.name}>
                <button
                  type="button"
                  onClick={() => onTeammateClick?.(t.name)}
                  className="w-full flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-muted/40 transition-colors text-left cursor-pointer"
                >
                  <span className="w-6 h-6 rounded-full bg-muted text-xs font-medium text-muted-foreground flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <span className="flex-1 text-sm truncate">{t.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{t.convos}</span>
                  <span className="text-xs text-muted-foreground shrink-0 w-10 text-right">
                    {t.hours}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
